import { useCallback, useEffect, useMemo, useState, memo } from "react";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import {
  PRICE_SCHEDULE_MODES,
  NO_CHANGE_OF_INFO,
  ROUTE_PATHS,
} from "../../common_nextgen/utils/Constants";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import { formFields } from "./OutofPocketConfig";
import useCrossAppSlices from "../../common_nextgen/common_components/common-hooks/useCrossAppSlices";
import useAiProgramBackNavigation from "../../common_nextgen/common_components/common-hooks/useAiProgramBackNavigation";

const screenInfo = {
  ADD: ["Add Out Of Pocket Schedule", "RCOPC003"],
  CHANGE: ["Out Of Pocket Max Schedule Detail", "RCOPC002"],
  VIEW: ["Out Of Pocket Max Schedule Detail", "RCOPC002"],
};

const OutOfPocketForm = ({ mode }) => {
  const { axios_helper_no_header } = useAxios();
  const navigateToPage = useCustomNavigate();
  const [constraintErrors, setConstraintErrors] = useState([]);
  const toast = useToast();

  const {
    currentState,
    previousState,
    setStateVal,
    currentMode,
    setCurrentMode,
    initialFormData,
    setInitialFormData,
    lookupData,
    fetchLookupData,
    viewLookupDetails,
    currentIndex,
    setHandleBack,
    onFetchInitialData,
    isNoChangeOfInfo,
    location,
    externalMode,
    paramsData,
  } = useCrossAppSlices({
    mode,
    recordsKey: "selectedSchedules",
  });

  const {
    selectedSchedules = [],
    previousScreen = ROUTE_PATHS.OUT_OF_POCKET_MAX_SCHEDULE,
    message = "",
    returnProgramCode = "",
    mediatorId = "",
  } = currentState;

  const { isAiProgramReturn, handleBackToAiProgram } =
    useAiProgramBackNavigation({
      programCode: returnProgramCode,
      mediatorId,
      paramsData,
    });

  const customFetchLookupData = (lookupScreen, params, formData) => {
    const updatedFormData = { ...formData };
    if (lookupScreen.default === "RCPLN018" && formData.planCode === "") {
      updatedFormData.planEffDate = "";
      updatedFormData.planName = "";
    }
    fetchLookupData(lookupScreen, params, updatedFormData);
  };

  const handleBack = () => {
    if (externalMode) {
      const message = {
        message: "navigate-to-external-program",
        url: previousScreen,
        parameters: {
          state: [...previousState],
        },
      };
      window.parent.postMessage(message, "*");
    } else if (currentIndex < selectedSchedules.length - 1) {
      setHandleBack();
    } else if (
      message === "navigate-to-ai-new-program" &&
      isAiProgramReturn
    ) {
      handleBackToAiProgram();
    } else {
      navigateToPage(previousScreen, previousState);
    }
  };

  const getPageTitle = useCallback(() => {
    const [screenTitle, screenNumber] = isAddMode(currentMode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  }, [currentMode]);

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";
    let infoChanged = true;

    if (isAddMode(currentMode)) {
      url = "api/opcOutPoktMaxSchCtrl/create";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/opcOutPoktMaxSchCtrl/update";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (isNoChangeOfInfo(dataForSubmission)) {
        infoChanged = false;
      }
    }

    const data = { ...dataForSubmission, screenNumber, infoChanged };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null,
      );
      const severity =
        responseStatus.message === NO_CHANGE_OF_INFO ? "info" : "success";
      toast.current.show({
        severity,
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });

      if (isAddMode(currentMode)) {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
        const lastRecord = previousState.pop() ?? {};
        const latestCurrentState = {
          ...currentState,
          selectedSchedules: [{ ...responseData }],
          currentIndex: 0,
        };
        const updatedState = [
          ...previousState,
          {
            ...lastRecord,
            paramsData: {
              opcScheduleName: responseData.opcScheduleName,
              hiddenRecordIndicator: "Active",
            },
          },
          latestCurrentState,
        ];
        setStateVal(updatedState);
        setInitialFormData(responseData);
        navigateToPage(
          ROUTE_PATHS.EDIT_OUT_OF_POCKET_MAX_SCHEDULE,
          updatedState,
        );
      } else {
        setInitialFormData(responseData);
      }
    } catch (error) {
      const {
        responseStatus: { displayErrorFields = null, message },
      } = error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const navigateToSteps = () =>
    navigateToPage(ROUTE_PATHS.OUT_OF_POCKET_MAX_SCHEDULE_STEPS, [
      ...previousState,
      { ...currentState, currentIndex, currentMode, externalMode },
      {
        selectedSchedules: [selectedSchedules[currentIndex]],
        currentIndex: 0,
        previousScreen: location.pathname,
      },
    ]);

  const navigateToNote = () =>
    navigateToPage(ROUTE_PATHS.OUT_OF_POCKET_MAX_SCHEDULE_NOTE, [
      ...previousState,
      { ...currentState, currentIndex, currentMode, externalMode },
      {
        selectedSchedules: [selectedSchedules[currentIndex]],
        currentIndex: 0,
        deccription: selectedSchedules[currentIndex]?.opcDescription,
        previousScreen: location.pathname,
      },
    ]);

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};
    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.F7 = navigateToSteps;
      keyName.F7 = "Steps";
      keyFunc.F14 = navigateToNote;
      keyName.F14 = "Note";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.chmod = "View";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.F7 = navigateToSteps;
      keyName.F7 = "Steps";
      keyFunc.F14 = navigateToNote;
      keyName.F14 = "Note";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.chmod = "Edit";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex, externalMode, selectedSchedules]);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!isAddMode(currentMode) && !selectedSchedules?.[currentIndex]) return;

      const url = isAddMode(currentMode)
        ? "api/opcOutPoktMaxSchCtrl/initial-form-data"
        : `api/opcOutPoktMaxSchCtrl/${encodeURIComponent(
            selectedSchedules[currentIndex].opcScheduleName,
          )}`;
      try {
        const { responseData } = await axios_helper_no_header("GET", url);
        setInitialFormData(responseData);
      } catch (e) {
        console.error(e);
      }
    };

    onFetchInitialData(fetchInitialData);
  }, [currentIndex, currentMode, onFetchInitialData, selectedSchedules]);

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
          fetchLookupData={customFetchLookupData}
          viewLookupDetails={viewLookupDetails}
          lookupData={lookupData}
        />
      </div>
    </div>
  );
};

export default memo(OutOfPocketForm);
